import { createPortal } from '@attensi/api-js'
import { AttensiConfig } from '@attensi/core'
import { urls } from '@attensi/urls'

import { Policy, convertPolicies, hasContent, getContent, hasLocale, hasLanguage } from './policy'

export interface AppData {
  policies: Policy[];
  locale: string | null;
}

export const getAppData = async (config: AttensiConfig, companyCode: string | null): Promise<AppData> => {
  try {
    const portal = createPortal(config)

    /**
     * If no subdomain, fetch using company_name_key or companyCode
     *
     * companyCode is first element in path
     */
    if (!config.subdomain) {
      if (config.company_name_key) {
        const data = await portal.getPublicCompaniesCode(config.company_name_key)
        // eslint-disable-next-line @typescript-eslint/camelcase
        portal.company_name_key = data.company_name_key
        portal.subdomain = data.subdomain
      } else if (companyCode) {
        const data = await portal.getPublicCompaniesCode(companyCode)
        // eslint-disable-next-line @typescript-eslint/camelcase
        portal.company_name_key = data.company_name_key
        portal.subdomain = data.subdomain
      }
    }

    /**
     * Fetch companies current, and get privacy policies
     */
    const company = await portal.getPublicCompaniesCurrent()
    const companyPrivacyPolicies = company.privacy_policies

    /**
     * Convert and filter policies
     */
    const convertedPolicies = await convertPolicies(companyPrivacyPolicies)
    const policies = convertedPolicies
      .filter(hasContent)
      .filter(hasLanguage)
      .filter(hasLocale)

    /**
     * Throw error if no policies
     */
    if (policies.length === 0) {
      throw Error('Unable to locate policies')
    }

    /**
     * Return AppData
     */
    return {
      policies,
      locale: config.locale || null
    }
  } catch (error) {
    /**
     * If error, return fallback privacy policy as AppData
     */
    const fallbackHtmlContent = await getContent(urls.legalDefaultPrivacyPolicy()) as string
    return {
      policies: [{
        html: fallbackHtmlContent,
        language: 'English',
        locale: 'en-US'
      }],
      locale: null
    }
  }
}
